import { isPlatformOrigin } from "@/lib/platformAuth";

/** Same event the platform shell bell already listens for. */
export const PRODUCT_NOTIFICATIONS_EVENT = "ndc:product-notifications";

export const KNOWLEDGE_ASSISTANT_PRODUCT_CONTEXT = "knowledge-assistant";

export interface ProductNotificationItem {
  id: string;
  text: string;
  createdAt: string;
  read: boolean;
  actor?: string | null;
  type?: string;
  href?: string | null;
}

export interface ProductNotifications {
  productContext: string;
  unreadCount: number;
  items: ProductNotificationItem[];
  viewAllHref?: string | null;
  viewAllLabel?: string | null;
}

type NotificationWindow = Window & {
  __ndcProductNotifications?: ProductNotifications;
};

/**
 * Tell the platform bell about this app's notices, the same way the other
 * apps do. Inside the hub the assistant is a separate frame, so the shell
 * only receives this when the browser allows it.
 */
export function publishProductNotifications(payload: ProductNotifications): void {
  if (typeof window === "undefined") return;
  publishOn(window, payload);
  const parent = window.parent;
  if (!parent || parent === window) return;
  const message = { type: PRODUCT_NOTIFICATIONS_EVENT, detail: payload };
  for (const origin of parentOrigins()) {
    try {
      parent.postMessage(message, origin);
    } catch {
      /* The parent origin refused the message. */
    }
  }
  try {
    publishOn(parent, payload);
  } catch {
    /* A cross-origin parent cannot take a direct event. */
  }
}

function publishOn(target: Window, payload: ProductNotifications): void {
  const host = target as NotificationWindow;
  host.__ndcProductNotifications = payload;
  target.dispatchEvent(new CustomEvent(PRODUCT_NOTIFICATIONS_EVENT, { detail: payload }));
}

function parentOrigins(): string[] {
  const origins = new Set<string>();
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors) {
      for (const origin of Array.from(ancestors)) {
        if (isPlatformOrigin(origin)) origins.add(origin);
      }
    }
  } catch {
    /* ancestorOrigins is not available in every browser. */
  }
  try {
    if (document.referrer) {
      const origin = new URL(document.referrer).origin;
      if (isPlatformOrigin(origin)) origins.add(origin);
    }
  } catch {
    /* Ignore a referrer that is not a URL. */
  }
  if (origins.size === 0) {
    origins.add("https://app.nationaldatacare.com");
    origins.add("https://app-dev.nationaldatacare.com");
  }
  return Array.from(origins);
}

export function openNoticeHref(href: string): void {
  if (typeof window === "undefined" || !href) return;
  if (/^https:\/\//.test(href)) {
    window.open(href, "_top", "noopener");
    return;
  }
  if (!href.startsWith("/") || href.startsWith("//")) return;
  const origin = parentOrigins()[0];
  if (!origin || window.parent === window) return;
  window.open(`${origin}${href}`, "_top", "noopener");
}
